import FA from 'admin/components/atoms/FontAwesome';
import Breadcrumb from 'admin/components/shared/Breadcrumb';
import Pager from 'admin/components/shared/Pager';
import { canShowShop } from 'admin/routes';
import { downloadCsvFile, formatUUID, toQueryParams } from 'admin/utils';
import React, { useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import {
  BooleanParam,
  NumberParam,
  QueryParamConfig,
  StringParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { fetchShopsCSV, getShops } from '~/javascripts/admin/actions/shops';
import getQueryParamDefaultValues from '~/javascripts/admin/utils/use-query-params';
import { useAppDispatch, useAppSelector } from '../../../hooks/useApp';
import { selectUserPrivateMoney } from '../../../reducers/user-private-money';
import { createBadge } from '../../atoms/Badge';
import Button from '../../atoms/Buttons';
import Headings from '../../atoms/Headings';
import Loader from '../../atoms/Loader';
import Table from '../../atoms/Table';
import FilterForm from './FilterForm';

type ShopsQueryParamConfig = typeof queryParamConfigs;
export type ShopsQueryParams = {
  [key in keyof ShopsQueryParamConfig]: ShopsQueryParamConfig[key] extends QueryParamConfig<
    unknown,
    infer T
  >
    ? T
    : never;
};

type Header = {
  label: string;
  size: number;
  align: 'center' | 'justify' | 'left' | 'right';
};

const PageWrapper = styled.div``;

const LoaderWrapper = styled.div`
  width: 100%;
  height: 150px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SearchResultInfo = styled.div`
  margin-top: 0.5rem;
  display: flex;
  align-items: baseline;
  gap: 16px;
`;

const SearchResultTitle = styled(Headings.h5)`
  margin: 0;
  margin-left: 15px;
  padding: 16px 0;
`;

const SearchResultCount = styled.span`
  flex: 1;
`;

const ShopInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const queryParamConfigs = {
  shop_name: withDefault(StringParam, null),
  shop_address: withDefault(StringParam, null),
  shop_tel: withDefault(StringParam, null),
  shop_email: withDefault(StringParam, null),
  is_include_members: withDefault(BooleanParam, false),
  page: withDefault(NumberParam, 1),
};

const queryParamDefaultValues = getQueryParamDefaultValues(queryParamConfigs);

const ShopStatusBadge = createBadge({
  active: { label: '有効' },
  disabled: {
    label: '無効',
    bgColor: 'grey',
  },
});

const HeadOfficeBadge = createBadge(
  {
    isHeadOffice: { label: '本店', bgColor: '#3995FF', isRounded: false },
  },
  {
    styles: {
      borderRadius: '9999px',
      padding: '2px 12px',
      textAlign: 'center',
    },
  }
);

const crumb = [
  { value: 'ホーム', href: '/', active: false },
  { value: '店舗管理', href: '', active: true },
  { value: '店舗一覧', href: '', active: true },
];

const headers: Header[] = [
  { label: '状態', size: 1, align: 'left' },
  { label: '店舗情報', size: 4, align: 'left' },
  { label: '住所', size: 4, align: 'left' },
  { label: '電話番号', size: 1.5, align: 'left' },
  { label: '外部ID', size: 1.5, align: 'left' },
];

const SettingsShop = () => {
  const { isLoading, shops, user } = useAppSelector((state) => ({
    isLoading: state.shop.isLoading,
    shops: state.shop.shops,
    user: state.user,
  }));
  const userPrivateMoney = useAppSelector(selectUserPrivateMoney);

  const dispatch = useAppDispatch();

  const [query, setQuery] = useQueryParams({
    ...queryParamConfigs,
  });

  const clearQuery = () => {
    setQuery({ ...queryParamDefaultValues });
  };

  const fetchShops = useCallback(
    (query: ShopsQueryParams) => {
      dispatch(
        getShops(
          toQueryParams(
            {
              per_page: 30,
              with_details: true,
              with_disabled: true,
              private_money_id: userPrivateMoney?.id,
              ...query,
            },
            true
          )
        )
      );
    },
    [getShops, userPrivateMoney.id]
  );

  const onChangePage = (page: number) => setQuery((prev) => ({ ...prev, page }));

  useEffect(() => {
    fetchShops(query);
  }, [query, userPrivateMoney.id]);

  const onDownloadShopCSV = () => {
    const { page, ...restQuery } = query;
    fetchShopsCSV(
      toQueryParams(
        { ...restQuery, private_money_id: userPrivateMoney?.id, with_disabled: true },
        true
      )
    ).then((res) => {
      downloadCsvFile(res.url);
    });
  };

  return (
    <PageWrapper>
      <Breadcrumb items={crumb} />
      <Headings.h5>店舗検索</Headings.h5>
      <FilterForm query={query} onSearch={setQuery} onClear={clearQuery} />
      <SearchResultInfo>
        <SearchResultTitle>検索結果</SearchResultTitle>
        {!!shops && <SearchResultCount>件数：{shops?.count ?? 0}</SearchResultCount>}
        <Button variant="muted-outline" onClick={onDownloadShopCSV}>
          <FA style={{ marginRight: '0.5em' }} icon="download" />
          CSV
        </Button>
      </SearchResultInfo>
      {isLoading ? (
        <LoaderWrapper>
          <Loader type="Oval" width={50} height={50} />
        </LoaderWrapper>
      ) : (
        <Table styles={{ marginBottom: '1rem' }}>
          <Table.Header>
            <Table.Row>
              {headers.map((c) => (
                <Table.Cell size={c.size} key={c.label} align={c.align}>
                  {c.label}
                </Table.Cell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {shops?.rows?.map((shop) => (
              <Table.Row
                key={shop.id}
                styles={{ background: shop.status === 'disabled' ? '#ddd' : 'unset' }}
              >
                <Table.Cell wordBreak="keep-all">
                  <ShopStatusBadge status={shop.status} />
                </Table.Cell>
                <Table.Cell wordBreak="break-word">
                  <ShopInfoWrapper>
                    <span>
                      {shop.name} {shop.is_head_office && <HeadOfficeBadge status="isHeadOffice" />}
                    </span>
                    {user?.role && canShowShop.includes(user.role) ? (
                      <Link to={`/shops/${shop.id}`}>{formatUUID(shop.id)}</Link>
                    ) : (
                      <span>{formatUUID(shop.id)}</span>
                    )}
                  </ShopInfoWrapper>
                </Table.Cell>
                <Table.Cell wordBreak="break-word">{shop.address}</Table.Cell>
                <Table.Cell wordBreak="keep-all">{shop.tel}</Table.Cell>
                <Table.Cell wordBreak="break-word">{shop.external_id}</Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
          <Table.Footer>
            <Table.Row noBorder>
              <Table.Cell colSpan={7} align="center" styles={{ paddingTop: '16px' }}>
                <Pager pagination={shops.pagination} onChangePage={onChangePage} />
              </Table.Cell>
            </Table.Row>
          </Table.Footer>
        </Table>
      )}
    </PageWrapper>
  );
};

export default SettingsShop;
